import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { StartAction } from '../../../Model/Dto/StartAction';
import { Message } from '../../../Model/System/Message';
import { TranslateService } from '@ngx-translate/core';
import { UserActionBaseComponent } from './userAction-base.component';
import { Macro } from '../../../Model/ui/Macro';
import { JobService } from 'app/Services/job.service';
import { LoginService } from 'app/Services/login.service';
import { SystemService } from 'app/Services/system.service';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { UserActionMarkAsChangedComponent } from './userAction-markAsChanged/userAction-markAsChanged.component';
import { MacroService } from 'app/Services/CatalogManagement/macro.service';
import { MacroFavoriteService } from 'app/Services/CatalogManagement/macroFavorite.service';
import { SelectionChangedEvent } from 'devextreme/ui/list';
import {Router} from "@angular/router";

@Component({
    selector: 'userAction',
    templateUrl: 'userAction.component.html',
    styleUrls: ['./userAction.component.css'],
    standalone: false
})
export class UserActionComponent implements OnInit {
  @Input() macro: string;
  @Output() macroChange = new EventEmitter<string>();

  private _parameter?: string | any;
  @Input()
  set parameter(value: any) {
    this._parameter = value;
    console.log('[parameter set]', value, typeof value, new Error().stack); // stack zeigt dir die Aufruferkette
    if (this._actionComponent && value != null) {
      this._actionComponent.parseParameter(value);
    }
  }
  get parameter() { return this._parameter; }
  @Output() parameterChange = new EventEmitter<string>();

  @Input() editMode: boolean = false;
  @Output() editModeChange = new EventEmitter<boolean>();

  @Input() catalogId: string;

  @Output() onMacroStart = new EventEmitter<any>();
  @Output() onMacroSave = new EventEmitter<{ name: string, parameter: string }>();

  private _actionComponent: UserActionBaseComponent;

  @ViewChild(UserActionBaseComponent, { static: false })
  set actionComponent(component: UserActionBaseComponent) {
    this._actionComponent = component;
    if (component) {
      if (this.parameter) this._actionComponent.parseParameter(this.parameter);
    }
  }

  get actionComponent(): UserActionBaseComponent {
    return this._actionComponent;
  }

  macros: Macro[];
  dataSource: any = [];
  selectedItem: Macro;
  selectedItemKeys = [];

  readonly favoritesGroupKey = 'Favoriten';
  favoriteMacroNames = new Set<string>();
  private macroGroups: any[] = [];

  isLoading: boolean = false;
  isDelete: boolean = false;
  startMacroButtonEnabled = false;
  currentStage

  constructor(
    public translate: TranslateService,
    public jobService: JobService,
    public loginService: LoginService,
    public systemService: SystemService,
    public catalogService: CatalogService,
    public macroService: MacroService,
    public macroFavoriteService: MacroFavoriteService,
    private router: Router
  ) {
    this.startMacro = this.startMacro.bind(this);
  }

  async ngOnInit() {


    this.macroService.workflowMode = this.editMode;
    this.onToggleStartMacroButton = this.onToggleStartMacroButton.bind(this);
    var currentStage = this.getStageFromUrl(this.router.url);
    this.macros = await this.jobService.getMacros(this.loginService.currentCustomer.id, currentStage);
    if(this.editMode)
      this.macros = this.macros.filter(x=>!x.hideInWorkflow);
    this.macros.forEach((macro) => {
      if (macro.groups && macro.groups.length) {
        macro.descLong = this.translate.instant(macro.name + 'Long');
        macro.descShort = this.translate.instant(macro.name + 'Short');

        if (macro.name === 'SetWawiLines' && (this.loginService.hasAddonDpbImport || this.loginService.hasAddonDpbExport)) {
          macro.descLong = this.translate.instant(this.loginService.hasAddonBauvista ? 'SetWawiLinesLongDpbBauvista' : 'SetWawiLinesLongDpb');
        }

        macro.groups.forEach((group) => {
          let groupObj = this.macroGroups.find((g) => g.key === group);
          if (!groupObj) {
            groupObj = { key: group, items: [] };
            this.macroGroups.push(groupObj);
          }

          groupObj.items.push({ ...macro });
        });
      }
    });

    await this.loadFavorites();
    this.rebuildDataSource();

    if (this.selectedItemKeys.length > 0) this.selectedItemKeys.splice(0, this.selectedItemKeys.length);
    this.selectedItemKeys.push(this.macro);
  }

  private async loadFavorites() {
    try {
      const favorites = await this.macroFavoriteService.getFavorites();
      this.favoriteMacroNames = new Set(favorites ?? []);
    } catch {
      this.favoriteMacroNames = new Set();
    }
  }

  private rebuildDataSource() {
    const favoriteItems = this.macros
      .filter((macro) => macro.groups?.length && !!macro.name && this.favoriteMacroNames.has(macro.name))
      .map((macro) => ({ ...macro }));

    this.dataSource = favoriteItems.length
      ? [{ key: this.favoritesGroupKey, items: favoriteItems }, ...this.macroGroups]
      : [...this.macroGroups];
  }

  isFavorite(macro: Macro): boolean {
    return !!macro?.name && this.favoriteMacroNames.has(macro.name);
  }

  async toggleFavorite(event: any, macro: Macro) {
    event?.event?.stopPropagation?.();
    const name = macro?.name;
    if (!name) return;

    if (this.favoriteMacroNames.has(name)) {
      this.favoriteMacroNames.delete(name);
      this.rebuildDataSource();
      await this.macroFavoriteService.removeFavorite(name);
    } else {
      this.favoriteMacroNames.add(name);
      this.rebuildDataSource();
      await this.macroFavoriteService.addFavorite(name);
    }
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    if (!event?.addedItems || event.addedItems.length == 0) return;

    this.selectedItem = event.addedItems[0];
    if (this.selectedItemKeys.length > 0) this.selectedItemKeys.splice(0, this.selectedItemKeys.length);
    this.selectedItemKeys.push(this.selectedItem.name);
  }

  onToggleStartMacroButton(enable: boolean) {
    this.startMacroButtonEnabled = enable;
  }

  isFeatureSystemMakro(macro: Macro): boolean {
    return macro?.groups?.some((x) => x == 'FeatureSystem') ?? false;
  }

  get targetCatalogId(): string {
    return this.catalogId ?? this.catalogService.catalog?.id;
  }

  async startMacro() {
    this.macroService.targetCatalogId = this.targetCatalogId;

    if (this.actionComponent && !(await this.actionComponent.canStartMacro())) {
      return;
    }

    const startAction = new StartAction();
    startAction.catalogId = this.targetCatalogId;
    startAction.customerId = this.loginService.currentUser.customerId;
    if (this.selectedItem.jobType) startAction.actionType = this.selectedItem.jobType;
    else {
      if (this.actionComponent) startAction.actionId = this.actionComponent.getActionId(this.selectedItem);
      else startAction.actionId = this.selectedItem.name;
    }

    if (this.actionComponent) startAction.actionParameter = this.actionComponent.createParameterStringAsJsonString();

    this.onMacroStart.emit();
    this.jobService.startUserAction(startAction).subscribe((success) => {
      if (success) {
        this.systemService.notify(new Message(this.translate.instant('Das Makro wurde gestartet')));
      }
    });
  }

  get showPlaceHolders() {
    return this.loginService.showPlaceHolders;
  }

  searchValue = '';
  accordionVisible = false;
  onContentReady(e) {
    if (this.searchValue?.length > 0) return;

    var items = e.component.option('items');
    if (items?.length == 0) return;

    for (var i = 0; i < items.length; i++) {
      if (items[i].key === this.favoritesGroupKey) {
        continue;
      }
      if (this.selectedItem &&   this.selectedItem.groups?.some(x=>x == items[i].name)) {
        continue;
      }
      e.component.collapseGroup(i);
    }
    this.accordionVisible = true;
  }

  saveMacro() {
    let macroSetting = { name: this.selectedItem.name, parameter: null };
    if (this.actionComponent) {
      macroSetting.parameter = this.actionComponent.createParameterStringAsJsonString();
    }
    this.onMacroSave.emit(macroSetting);
  }

  openWikiUrl(): void {
    if (this.selectedItem?.wikiUrl) {
      window.open(this.selectedItem.wikiUrl, '_blank');
    }
  }

  getStageFromUrl(url: string): string {
    var splitString = url.split('/');
    var stagePart = splitString[2];

    return stagePart;
  }
  
}
